import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import {
  ExclamationCircleFilled,
  DollarOutlined,
  AuditOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Popover } from 'antd';

import generatePath from '~settings/routing';
import { bookingJourneyType } from 'common/constants/booking';

const useStyles = makeStyles(
  theme => ({
    wrapper: {
      ...theme.flex.crossCenter,
      backgroundColor: theme.colors.orange1,
      borderRadius: 2,
      padding: '6px 12px',
      ...theme.typography.body4,
      color: theme.colors.orange6,
    },

    popContent: {
      '& > .ant-popover-content': {
        borderRadius: 2,
        '& > .ant-popover-arrow': {
          right: 22,
          borderRightColor: 'rgba(0, 0, 0, 0.75)',
          borderBottomColor: 'rgba(0, 0, 0, 0.75)',
        },
        '& > .ant-popover-inner': {
          padding: 8,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',

          '& .ant-popover-inner-content': {
            padding: 0,
            color: theme.colors.gray1,
          },
        },
      },
    },

    tipIcon: {
      marginRight: 8,
      color: theme.colors.orange11,

      '& > svg': {
        width: 12,
        height: 12,
      },
    },

    tipButton: {
      ...theme.typography.body6,
      ...theme.flex.crossCenter,
      width: 'fit-content',
      border: `1px solid ${theme.colors.orange2}`,
      borderRadius: 2,
      padding: '0 8px',
      margin: '0 6px',
      cursor: 'pointer',

      '&:hover': {
        borderColor: theme.colors.gray5,
        backgroundColor: theme.colors.gray1,
        color: theme.colors.bodyColor,

        '& > span:first-child': {
          color: theme.colors.disableColor,
        },
      },
    },

    tipButtonIcon: {
      display: 'flex',
      alignItems: 'center',
      marginRight: 6,

      '& > svg': {
        width: 12,
        height: 12,
      },
    },

    tipButtonRightIcon: {
      marginLeft: 1,

      '& > svg': {
        width: 8,
        height: 8,
      },
    },

    subContent: {
      marginRight: 6,
    },
  }),
  { name: 'pendingAlertTips' },
);

const PendingAlertTips = ({ property, history, from }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const {
    publishCondition: { commissionTier, contractStage, paymentPlan, payout, offlinePaymentMethod },
  } = property;
  const paymentOnline = !property?.paymentPlan || property?.paymentPlan?.paymentOnline;
  const isManual = [bookingJourneyType.MANUAL, bookingJourneyType.FULL_AUTOMATIC].includes(
    property?.bookingJourney,
  );
  const contractUrl = generatePath('landlord', { landlordSlug: property?.landlord?.slug });
  const commissionUrl = generatePath(
    'landlord',
    { landlordSlug: property?.landlord?.slug },
    { tab: 'COMMISSION' },
  );

  const handleGotoSet = key => {
    history.push(key === 'contract' ? contractUrl : commissionUrl);
  };

  const labelContent = key => (
    <Popover
      overlayClassName={styles.popContent}
      content={t(`common.pending_alert_tips.content.${key}`)}
    >
      <span className={styles.tipButton} onClick={() => handleGotoSet(key)}>
        {key === 'contract' ? (
          <AuditOutlined className={styles.tipButtonIcon} />
        ) : (
          <DollarOutlined className={styles.tipButtonIcon} />
        )}
        <div>{t(`common.pending_alert_tips.${key}`)}</div>
        <RightOutlined className={styles.tipButtonRightIcon} />
      </span>
    </Popover>
  );

  const showPaymentMethod = useMemo(() => {
    if (paymentPlan && !paymentOnline) {
      return true;
    }
    return false;
  }, [paymentPlan, property]);

  const isSetted = useMemo(
    () => (showPaymentMethod ? offlinePaymentMethod : payout),
    [payout, offlinePaymentMethod, showPaymentMethod],
  );

  if (commissionTier && contractStage && paymentPlan && isSetted) {
    return null;
  }

  return (
    <div className={styles.wrapper} style={{ marginBottom: from === 'propertyHomepage' && 24 }}>
      <ExclamationCircleFilled className={styles.tipIcon} />
      <If condition={!commissionTier || !contractStage}>
        <span>{t('common.pending_alert_tips.content.pre_content')}</span>
        <Choose>
          <When condition={!commissionTier && !contractStage}>
            {labelContent('commission')}
            and {labelContent('contract')}
          </When>
          <When condition={!commissionTier}>{labelContent('commission')}</When>
          <When condition={!contractStage}>{labelContent('contract')}</When>
        </Choose>
        <span className={styles.subContent}>
          {t('common.pending_alert_tips.content.sub_content')}
        </span>
      </If>
      <If condition={!isManual}>
        <Choose>
          <When condition={!paymentPlan && paymentOnline && !payout}>
            <span>{t('common.pending_alert_tips.content.payment_and_payout.tip')}</span>
          </When>
          <When condition={!paymentPlan && !paymentOnline && !offlinePaymentMethod}>
            <span>{t('common.pending_alert_tips.content.payment_and_payment_method.tip')}</span>
          </When>
          <When condition={!paymentPlan}>
            <span>{t('common.pending_alert_tips.content.payment_plan.tip')}</span>
          </When>
          <When condition={paymentOnline && !payout}>
            <span>{t('common.pending_alert_tips.content.payout_plan.tip')}</span>
          </When>
          <When condition={paymentPlan && !paymentOnline && !offlinePaymentMethod}>
            <span>{t('common.pending_alert_tips.content.payment_method.tip')}</span>
          </When>
        </Choose>
      </If>
    </div>
  );
};

PendingAlertTips.propTypes = {
  property: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default PendingAlertTips;
